import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import * as api from '@/api/mock/adapter'
import type { User } from '@/types'
import { AuthContext, type AuthState } from './context'

/**
 * Dev-only session holder, backed by the mock adapter's sessionStorage flag.
 *
 * This is reachable from `npm run dev` and from vitest, and from nowhere else:
 * `loadRuntimeConfig` only returns mock mode under `import.meta.env.DEV`, which
 * is the literal `false` in a production build. To run local dev against a real
 * tenant instead, set VITE_AUTH0_* in `.env.local`.
 *
 * This component is still PRESENT in the production bundle — `AuthProvider`
 * imports it statically, so rollup cannot drop it. It is unreachable, not
 * absent: nothing in a deployed build can produce a config with `mode: 'mock'`,
 * because the only two producers are the dropped DEV branch and a zod schema
 * that rejects it. Finding this code in a deployed bundle is expected.
 */
export function MockAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .getSession()
      .then((session) => {
        if (!cancelled) setUser(session)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async () => {
    setError(null)
    try {
      setUser(await api.signIn())
    } catch {
      // Generic on purpose. Distinguishing "no account" from "wrong domain"
      // enumerates the directory (docs/03-page-specs.md §1).
      setError('That sign-in did not go through. Try again, or contact your administrator.')
      throw new Error('sign-in failed')
    }
  }, [])

  const signOut = useCallback(async () => {
    await api.signOut()
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, loading, error, signIn, signOut }),
    [user, loading, error, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
